import type { TRPCLink } from '@trpc/client';
import type { AnyRouter } from '@trpc/server';
import { observable } from '@trpc/server/observable';

import type { LogoutRedirector } from './authHeaders';
import { isTrpcUnauthorizedError } from './classification';

/**
 * Drives re-authentication when the *server* rejects the session, rather than
 * when the client fails to renew it (COI-159).
 *
 * `createTrpcAuthHeaders` already covers the request side — a silent refresh
 * that fails never sends the request at all. This is the other half: a token
 * that the client still holds and believes in, which the server no longer
 * accepts. Before this link the response simply surfaced as a failed query, so
 * an expired session showed as a broken page rather than a login prompt.
 *
 * **A 401 arrives in several different shapes and all of them are handled
 * here.** A `TRPCError({ code: 'UNAUTHORIZED' })` thrown from `context.ts`
 * (malformed claims) or `init.ts` (no user) goes through tRPC's error formatter
 * and arrives as a proper envelope with `data.httpStatus === 401`. `express-jwt`
 * is mounted with no error handler (`packages/trpc/src/trpc-server.ts`), so an
 * expired or malformed JWT is instead rejected by Express's `finalhandler` with
 * an HTML body — which `@trpc/client` cannot parse, and whose status it drops
 * along with the response it never got to record. `createStatusPreservingFetch`
 * (./statusPreservingFetch) is what keeps that status alive, on the error's
 * `cause`. `isTrpcUnauthorizedError` reads whichever route carries it, so no
 * shape slips past.
 *
 * The link sits before `splitLink` in each app's `TrpcProvider`, so it observes
 * every leg of the chain. It never swallows anything: the error is always
 * forwarded so react-query settles the query and `QueryCache.onError` still
 * runs — the page is navigating away regardless.
 */
export const createTrpcAuthRedirectLink =
  <TRouter extends AnyRouter>({
    redirector,
  }: {
    /**
     * Shared with `createTrpcAuthHeaders`, so a fan-out that fails on both the
     * request and the response side still produces ONE navigation.
     */
    redirector: LogoutRedirector;
  }): TRPCLink<TRouter> =>
  () =>
  ({ op, next }) =>
    observable((observer) => {
      const subscription = next(op).subscribe({
        next: (value) => observer.next(value),
        error: (error) => {
          if (isTrpcUnauthorizedError(error)) {
            redirector.redirectOnce();
          }

          observer.error(error);
        },
        complete: () => observer.complete(),
      });

      return () => {
        subscription.unsubscribe();
      };
    });
